import { useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Info,
  Settings2,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import {
  commissionSurgery,
  useCommissionAccess,
  useCommissionAgents,
  useCommissionReport,
  useCommissionPreview,
  useCommissionApply,
  useCommissionAttributeBulk,
} from '../../shared/api/generated/commercial-commissions/commercial-commissions'
import type {
  CommissionAttributionOut,
  CommissionLine,
  CommissionReconcileOut,
} from '../../shared/api/generated/model'
import { Drawer } from '../../shared/ui/Drawer'
import { useToast } from '../../shared/ui/Toast'
import { AgentConfiguration } from './AgentConfiguration'
import { AttributionReview } from './AttributionReview'
import { CommissionDialog } from './CommissionDialog'
import { CommissionDrawer } from './CommissionDrawer'
import { CommissionTable, lineKey } from './CommissionTable'
import { competenceLabel, currentMonth, errorMessage, money, plural, shiftMonth } from './helpers'
import { useInvalidateCommissions } from './hooks'
import './commissions.css'

export function CommissionsPage() {
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  const toast = useToast()
  const competence = params.get('mes') || currentMonth()
  const personal = location.pathname.startsWith('/minhas-comissoes')
  const pendingOnly = location.pathname.endsWith('/pendencias')
  const basePath = personal ? '/minhas-comissoes' : '/comissoes-comercial'
  const sellerId = !personal && params.get('vendedor') ? Number(params.get('vendedor')) : undefined
  const access = useCommissionAccess({ query: { retry: false } })
  const allowed = personal
    ? !!access.data?.can_read_own || (pendingOnly && !!access.data?.can_review)
    : !!access.data?.can_manage
  const agents = useCommissionAgents({ query: { enabled: allowed && !personal } })
  const report = useCommissionReport(
    { competence, seller_id: sellerId, scope: personal ? 'own' : 'all' },
    {
      query: {
        enabled: allowed && (!personal || !!access.data?.can_read_own),
        refetchInterval: 60000,
      },
    },
  )
  const preview = useCommissionPreview()
  const apply = useCommissionApply()
  const bulk = useCommissionAttributeBulk()
  const invalidate = useInvalidateCommissions()
  const [review, setReview] = useState<CommissionReconcileOut | null>(null)
  const [openLine, setOpenLine] = useState<CommissionLine | null>(null)
  const [agentId, setAgentId] = useState<number | null>(null)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkCloser, setBulkCloser] = useState('')
  const [bulkReason, setBulkReason] = useState('')
  const [bulkError, setBulkError] = useState('')

  // Sem seleção não há popover: fecha junto quando a última cirurgia sai da seleção.
  const applySelection = (next: Set<number>) => {
    setSelected(next)
    if (next.size === 0) setBulkOpen(false)
  }

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next)
    setReview(null)
    setSelected(new Set())
  }
  const clearSelection = () => {
    setSelected(new Set())
    setBulkOpen(false)
    setBulkError('')
  }
  const toggleSelected = (id: number) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    applySelection(next)
  }
  const toggleMany = (ids: number[], checked: boolean) => {
    const next = new Set(selected)
    ids.forEach((id) => (checked ? next.add(id) : next.delete(id)))
    applySelection(next)
  }
  const saveBulk = async () => {
    if (!bulkCloser || bulkReason.trim().length < 3) {
      setBulkError('Selecione o fechador e informe o motivo.')
      return
    }
    setBulkError('')
    try {
      const contexts = await Promise.all([...selected].map((id) => commissionSurgery(id)))
      const targets = new Map<
        string,
        { surgeryId: number; attribution: CommissionAttributionOut }
      >()
      contexts.forEach((c, i) => {
        const a = c.attributions[0]
        if (c.attributions.length !== 1) throw Error('Cirurgia com aditivos: corrija pelo deal.')
        targets.set(a.proposal_id ? `p-${a.proposal_id}` : `s-${a.legacy_surgery_id}`, {
          surgeryId: [...selected][i],
          attribution: a,
        })
      })
      await bulk.mutateAsync({
        data: {
          items: [...targets.values()].map((t) => ({
            surgery_id: t.surgeryId,
            closer_user_id: Number(bulkCloser),
            expected_revision: t.attribution.revision,
            reason: bulkReason.trim(),
          })),
        },
      })
      await invalidate()
      clearSelection()
      setBulkCloser('')
      setBulkReason('')
      setReview(null)
      toast.success(
        'Fechadores atualizados. As correções serão consideradas na apuração, sem aprovação intermediária.',
      )
    } catch (e) {
      setBulkError(errorMessage(e))
    }
  }
  const runPreview = async () => {
    try {
      setReview(
        await preview.mutateAsync({
          data: { competence_from: competence, competence_to: competence },
        }),
      )
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }
  const applyReview = async () => {
    if (!review) return
    try {
      await apply.mutateAsync({
        data: {
          competence_from: competence,
          competence_to: competence,
          fingerprint: review.fingerprint,
        },
      })
      await invalidate()
      setReview(null)
      toast.success(`Apuração de ${competenceLabel(competence).toLowerCase()} aplicada.`)
    } catch (e) {
      toast.error(errorMessage(e))
      setReview(null)
    }
  }

  if (access.isLoading)
    return (
      <main className="cc-page">
        <div className="cc-empty" role="status">
          Carregando acesso…
        </div>
      </main>
    )
  if (
    !personal &&
    access.data &&
    !access.data.can_manage &&
    (access.data.can_read_own || access.data.can_review)
  )
    return (
      <Navigate
        replace
        to={`/minhas-comissoes${pendingOnly || !access.data.can_read_own ? '/pendencias' : ''}?mes=${competence}`}
      />
    )
  if (personal && !pendingOnly && access.data?.can_review && !access.data.can_read_own)
    return <Navigate replace to={`/minhas-comissoes/pendencias?mes=${competence}`} />
  if (!allowed)
    return (
      <main className="cc-page">
        <div className="cc-empty cc-empty-boxed">
          <p>Acesso restrito</p>
          <span>As comissões comerciais não estão autorizadas para o seu usuário.</span>
        </div>
      </main>
    )

  const data = personal && !access.data?.can_read_own ? undefined : report.data
  const canManage = !!access.data?.can_manage && !personal
  const canReview = !!access.data?.can_review
  const pendingCount = data?.pending_count ?? 0
  const processingCount = data?.processing_count ?? 0
  const saleRows = data?.entries?.filter((r) => r.trigger === 'sale_closed') ?? []
  const operatedRows = data?.entries?.filter((r) => r.trigger === 'surgery_operated') ?? []
  const activeAgent = agents.data?.find((a) => a.user_id === agentId)
  const activeKey = openLine ? lineKey(openLine) : null
  const monthLabel = competenceLabel(competence)

  return (
    <main className="cc-page">
      <header className="cc-header">
        <div>
          <h1 className="cc-title">
            {personal
              ? access.data?.can_read_own
                ? 'Minhas comissões'
                : 'Pendências de atribuição'
              : 'Comissões comerciais'}
          </h1>
          <p className="cc-subtitle">
            {personal && !access.data?.can_read_own
              ? 'Conferência dos responsáveis comerciais, sem acesso aos valores das comissões.'
              : personal
                ? 'Suas parcelas de venda e operação. Valores apurados não representam pagamentos.'
                : 'Comissão de cada venda fechada e cirurgia operada no mês.'}
          </p>
        </div>
        {canManage && (
          <div className="cc-actions">
            <button type="button" className="btn" onClick={() => setAgentId(0)}>
              <Settings2 size={14} aria-hidden="true" /> Configurar agentes
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={preview.isPending || apply.isPending}
              onClick={runPreview}
            >
              <ClipboardCheck size={14} aria-hidden="true" />
              {preview.isPending ? 'Preparando prévia…' : 'Conferir apuração do mês'}
            </button>
          </div>
        )}
      </header>

      <div className="cc-filters" role="group" aria-label="Filtros">
        <div className="cc-chip cc-chip-month">
          <button
            type="button"
            aria-label="Mês anterior"
            onClick={() => updateFilter('mes', shiftMonth(competence, -1))}
          >
            <ChevronLeft size={14} aria-hidden="true" />
          </button>
          <label>
            <span>{monthLabel}</span>
            <input
              type="month"
              aria-label="Competência"
              value={competence}
              onChange={(e) => e.target.value && updateFilter('mes', e.target.value)}
            />
          </label>
          <button
            type="button"
            aria-label="Próximo mês"
            onClick={() => updateFilter('mes', shiftMonth(competence, 1))}
          >
            <ChevronRight size={14} aria-hidden="true" />
          </button>
        </div>
        {canManage && (
          <label className={`cc-chip cc-chip-select${sellerId ? ' is-set' : ''}`}>
            <span className="cc-chip-key">Comercial</span>
            <select
              aria-label="Comercial"
              value={sellerId ?? ''}
              onChange={(e) => updateFilter('vendedor', e.target.value)}
            >
              <option value="">Todos</option>
              {agents.data?.map((a) => (
                <option key={a.user_id} value={a.user_id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <nav className="cc-tabs" aria-label="Visões">
        {(!personal || access.data?.can_read_own) && (
          <Link
            className={!pendingOnly ? 'is-active' : ''}
            aria-current={!pendingOnly ? 'page' : undefined}
            to={`${basePath}?${params}`}
          >
            {personal ? 'Minhas parcelas' : 'Visão geral'}
          </Link>
        )}
        <Link
          className={pendingOnly ? 'is-active' : ''}
          aria-current={pendingOnly ? 'page' : undefined}
          to={`${basePath}/pendencias?${params}`}
        >
          Pendências
          {pendingCount > 0 && <span className="cc-count is-warn">{pendingCount}</span>}
        </Link>
      </nav>

      {pendingOnly && canReview && !data && (
        <AttributionReview key={competence} competence={competence} />
      )}

      {report.isLoading && (
        <div className="cc-empty cc-empty-boxed" role="status">
          Carregando {monthLabel.toLowerCase()}…
        </div>
      )}
      {report.isError && (
        <p className="cc-notice cc-error" role="alert">
          {errorMessage(report.error)}
        </p>
      )}

      {data && (
        <>
          <div className="cc-kpis">
            <div className="cc-kpi is-total">
              <span className="cc-kpi-label">
                Total apurado
                <Info
                  size={12}
                  aria-label="Valores devidos sobre honorários médicos. A apuração reconhece a comissão e não representa um pagamento."
                  role="img"
                />
              </span>
              <strong>{money(data.total_cents)}</strong>
            </div>
            <div className="cc-kpi">
              <span className="cc-kpi-label">Vendas</span>
              <strong>{money(data.sale_cents)}</strong>
            </div>
            <div className="cc-kpi">
              <span className="cc-kpi-label">Operações</span>
              <strong>{money(data.operated_cents)}</strong>
            </div>
            <div className={`cc-kpi${pendingCount > 0 ? ' is-warn' : ''}`}>
              <span className="cc-kpi-label">Pendências</span>
              <strong>
                {pendingCount}
                {processingCount > 0 && (
                  <small> · {plural(processingCount, 'pronta', 'prontas')}</small>
                )}
              </strong>
            </div>
          </div>

          {pendingOnly && canReview && (
            <AttributionReview key={competence} competence={competence} />
          )}

          {!pendingOnly && (
            <>
              {!personal && (
                <section className="cc-card">
                  <div className="cc-card-head">
                    <div>
                      <h2>Por comercial</h2>
                      <p>Clique no nome para filtrar a página.</p>
                    </div>
                    <span className="cc-count">
                      {plural(data.sellers?.length ?? 0, 'comercial', 'comerciais')}
                    </span>
                  </div>
                  {!data.sellers?.length ? (
                    <div className="cc-empty">
                      <p>Nenhuma comissão apurada neste mês.</p>
                      <span>Use “Conferir apuração do mês” para gerar a prévia.</span>
                    </div>
                  ) : (
                    <div className="cc-table-wrap">
                      <table className="cc-table cc-summary">
                        <thead>
                          <tr>
                            <th>Comercial</th>
                            <th className="cc-num">Vendas</th>
                            <th className="cc-num">Operações</th>
                            <th className="cc-num">Total</th>
                            {canManage && (
                              <th className="cc-col-action">
                                <span className="cc-sr-only">Ações</span>
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {data.sellers?.map((s) => (
                            <tr
                              key={s.seller_user_id}
                              className={sellerId === s.seller_user_id ? 'is-active' : undefined}
                            >
                              <td>
                                <button
                                  type="button"
                                  className="cc-text-button"
                                  onClick={() =>
                                    updateFilter(
                                      'vendedor',
                                      sellerId === s.seller_user_id ? '' : String(s.seller_user_id),
                                    )
                                  }
                                >
                                  {s.seller_name}
                                </button>
                              </td>
                              <td className="cc-num">{money(s.sale_cents)}</td>
                              <td className="cc-num">{money(s.operated_cents)}</td>
                              <td className="cc-num cc-amount">{money(s.total_cents)}</td>
                              {canManage && (
                                <td className="cc-col-action">
                                  <button
                                    type="button"
                                    className="btn btn-sm cc-row-action"
                                    onClick={() => setAgentId(s.seller_user_id)}
                                  >
                                    <SlidersHorizontal size={12} aria-hidden="true" /> Percentuais
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              )}

              <section className="cc-card">
                <div className="cc-card-head">
                  <div>
                    <h2>Vendas fechadas no mês</h2>
                    <p>Parcela de venda. Preservada mesmo se a cirurgia for cancelada.</p>
                  </div>
                  <div className="cc-card-total">
                    <small>{plural(saleRows.length, 'parcela', 'parcelas')}</small>
                    <strong>{money(data.sale_cents)}</strong>
                  </div>
                </div>
                <CommissionTable
                  rows={saleRows}
                  context="sale"
                  onOpen={setOpenLine}
                  activeKey={activeKey}
                />
              </section>

              <section className="cc-card">
                <div className="cc-card-head">
                  <div>
                    <h2>Cirurgias operadas no mês</h2>
                    <p>Parcela de operação. Reconhecida pela data da cirurgia operada.</p>
                  </div>
                  <div className="cc-card-total">
                    <small>{plural(operatedRows.length, 'parcela', 'parcelas')}</small>
                    <strong>{money(data.operated_cents)}</strong>
                  </div>
                </div>
                <CommissionTable
                  rows={operatedRows}
                  context="operated"
                  onOpen={setOpenLine}
                  activeKey={activeKey}
                />
              </section>
            </>
          )}

          <section className="cc-card">
            <div className="cc-card-head">
              <div>
                <h2>
                  {personal
                    ? 'Minhas pendências e valores a apurar'
                    : 'Pendências e valores a apurar'}
                </h2>
                <p>Fora do total. Itens sem data de competência ficam aqui até a regularização.</p>
              </div>
              <span className="cc-count">{plural(data.pending?.length ?? 0, 'item', 'itens')}</span>
            </div>
            <CommissionTable
              rows={data.pending ?? []}
              onOpen={setOpenLine}
              activeKey={activeKey}
              selected={canManage ? selected : undefined}
              onSelect={canManage ? toggleSelected : undefined}
              onSelectAll={canManage ? toggleMany : undefined}
              emptyTitle="Nenhuma pendência neste mês."
              emptyHint="Todas as parcelas têm atribuição e percentual definidos."
            />
          </section>
        </>
      )}

      {canManage && selected.size > 0 && (
        <div className="cc-selection" role="region" aria-label="Seleção">
          {bulkOpen && (
            <form
              className="cc-popover"
              aria-label="Definir fechador"
              onSubmit={(e) => {
                e.preventDefault()
                void saveBulk()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setBulkOpen(false)
              }}
            >
              <div className="cc-popover-head">
                <strong>
                  Definir fechador para {plural(selected.size, 'cirurgia', 'cirurgias')}
                </strong>
                <button
                  type="button"
                  className="cc-icon-button"
                  aria-label="Fechar"
                  onClick={() => setBulkOpen(false)}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </div>
              <label className="cc-field" htmlFor="cc-bulk-closer">
                Fechador
                <select
                  id="cc-bulk-closer"
                  autoFocus
                  value={bulkCloser}
                  onChange={(e) => setBulkCloser(e.target.value)}
                >
                  <option value="">Selecionar</option>
                  {agents.data?.map((a) => (
                    <option key={a.user_id} value={a.user_id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="cc-field" htmlFor="cc-bulk-reason">
                Motivo
                <input
                  id="cc-bulk-reason"
                  value={bulkReason}
                  placeholder="Registrado no histórico de cada atribuição"
                  onChange={(e) => setBulkReason(e.target.value)}
                />
              </label>
              {bulkError && (
                <p className="cc-notice cc-error" role="alert">
                  {bulkError}
                </p>
              )}
              <div className="cc-form-actions">
                <button type="button" className="btn" onClick={() => setBulkOpen(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={bulk.isPending}>
                  {bulk.isPending ? 'Salvando…' : 'Salvar'}
                </button>
              </div>
            </form>
          )}
          <div className="cc-selection-bar">
            <span className="cc-selection-count">
              {plural(selected.size, 'cirurgia selecionada', 'cirurgias selecionadas')}
            </span>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              aria-expanded={bulkOpen}
              onClick={() => setBulkOpen((v) => !v)}
            >
              Definir fechador
            </button>
            <button type="button" className="btn btn-sm" onClick={clearSelection}>
              Limpar
            </button>
          </div>
        </div>
      )}

      {openLine && (
        <CommissionDrawer
          line={openLine}
          canReview={canReview}
          onClose={() => setOpenLine(null)}
          onSaved={() => {
            setOpenLine(null)
            setReview(null)
            toast.success('Atribuição atualizada e registrada no histórico.')
          }}
        />
      )}

      {canManage && agentId != null && (
        <Drawer
          open
          onClose={() => setAgentId(null)}
          width={520}
          title={
            <div className="drawer-title-block">
              <div className="drawer-title">{activeAgent?.name ?? 'Comercial'}</div>
              <div className="drawer-subtitle">Perfil comercial e percentuais de comissão</div>
            </div>
          }
        >
          <div className="cc-drawer">
            <label className="cc-field">
              Usuário
              <select value={agentId || ''} onChange={(e) => setAgentId(Number(e.target.value))}>
                <option value="">Selecione um usuário</option>
                {agents.data?.map((agent) => (
                  <option key={agent.user_id} value={agent.user_id}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </label>
            {agentId > 0 && <AgentConfiguration key={agentId} userId={agentId} />}
          </div>
        </Drawer>
      )}

      {canManage && review && (
        <CommissionDialog
          label={`Prévia da apuração de ${monthLabel}`}
          title={
            <>
              Prévia da apuração <span className="cc-dialog-context">{monthLabel}</span>
            </>
          }
          description="Abrange todos os comerciais do mês, independentemente do filtro. Nada é gravado até você aplicar."
          onClose={() => setReview(null)}
          busy={apply.isPending}
          wide
        >
          <div className="cc-review-summary">
            <div>
              <strong>{review.created}</strong>
              <span>{review.created === 1 ? 'lançamento a criar' : 'lançamentos a criar'}</span>
            </div>
            <div>
              <strong>{review.voided}</strong>
              <span>a substituir ou anular</span>
            </div>
            <div className={review.pending_count > 0 ? 'is-warn' : ''}>
              <strong>{review.pending_count}</strong>
              <span>{review.pending_count === 1 ? 'pendência' : 'pendências'}</span>
            </div>
          </div>
          <div className="cc-dialog-table">
            <CommissionTable
              rows={review.entries}
              emptyTitle="Nenhum lançamento a criar."
              emptyHint="A apuração deste mês já está em dia."
            />
          </div>
          <p className="cc-muted">
            A aplicação preserva o histórico. Pendências continuam fora do total.
          </p>
          <div className="cc-dialog-actions">
            <button
              type="button"
              className="btn"
              onClick={() => setReview(null)}
              disabled={apply.isPending}
            >
              Voltar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={apply.isPending}
              onClick={applyReview}
            >
              {apply.isPending ? 'Aplicando…' : 'Aplicar apuração'}
            </button>
          </div>
        </CommissionDialog>
      )}
    </main>
  )
}
